import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Caption1, ClockAddIcon, formatHours, ProgressPie, Tooltip } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import TimeRecordsListTaskModalContainer from "~/components/TimeRecordForm/TimeRecordsListTaskModalContainer";
import TimeRecordTrackedItemModalFormContainer from "~/components/TimeRecordForm/TimeRecordTrackedItemModalFormContainer";
import { getObjectTotalTime } from "~/store/selectors/selector.additionalObjectData";
import { getTaskById } from "~/store/selectors/selector.tasks";
import { canSeeTimeEstimates, isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import { calculateTimeToEstimatePercentage, formatTrackedAndEstimatedTime } from "~/utils/utils";
import { propTypes } from "./propTypes";

export const TimeTaskView = ({ hideIcon, tracked, estimated, showEmptyValue, enableTooltip }) => {
  const value = useMemo(() => {
    return formatTrackedAndEstimatedTime(tracked, estimated);
  }, [estimated, tracked]);

  const percent = useMemo(() => {
    return calculateTimeToEstimatePercentage(tracked, estimated);
  }, [estimated, tracked]);

  const timeTooltipValue = useMemo(() => {
    if (estimated < 1000 && tracked < 1000) {
      return 0;
    } else if (estimated > 0) {
      return `${formatHours(tracked, false, false)}/${formatHours(estimated, false, false)}`;
    } else {
      return formatHours(tracked, false, false);
    }
  }, [estimated, tracked]);

  const progressColor = () => {
    if (percent >= 100) {
      return "var(--red-alert)";
    }
    if (percent >= 75) {
      return "var(--warning)";
    }
    return "var(--color-sucess-green)";
  };

  return (
    <>
      {tracked || estimated ? (
        <>
          {estimated ? (
            <ProgressPie
              radius={10}
              progress={percent}
              className="tw-mr-2"
              progressColor={progressColor}
            />
          ) : null}
          <Tooltip
            title={timeTooltipValue}
            disable={!enableTooltip || timeTooltipValue === 0}
          >
            <Caption1 weight="bold">{value}</Caption1>
          </Tooltip>
        </>
      ) : (
        <>
          {!hideIcon ? <ClockAddIcon /> : null}
          {showEmptyValue && <Caption1 weight="bold">0:00</Caption1>}
        </>
      )}
    </>
  );
};

TimeTaskView.propTypes = {
  hideIcon: PropTypes.bool,
  tracked: PropTypes.number,
  estimated: PropTypes.number,
  showEmptyValue: PropTypes.bool,
  enableTooltip: PropTypes.bool,
};

const TimeEditableTaskView = ({ task, projectId, isLoggedUserClient, tracked, estimated }) => {
  const taskId = task.get("id");
  const { t } = useTranslation();
  const [openForm, setOpenForm] = useState(false);
  const [openRecordList, setOpenRecordList] = useState(false);

  const handleOpen = useCallback(
    (e) => {
      e.stopPropagation();
      !isLoggedUserClient ? setOpenForm(true) : setOpenRecordList(true);
    },
    [isLoggedUserClient]
  );

  const handleCloseForm = useCallback(() => {
    setOpenForm(false);
  }, []);

  const handleCloseRecordList = useCallback(() => {
    setOpenRecordList(false);
  }, []);

  return (
    <>
      {openForm && (
        <TimeRecordTrackedItemModalFormContainer
          handleDismiss={handleCloseForm}
          parentId={taskId}
          parentType="Task"
          projectId={projectId}
          open={openForm}
          displaySuccessToast={false}
        />
      )}

      {openRecordList && (
        <TimeRecordsListTaskModalContainer
          taskId={taskId}
          projectId={projectId}
          handleDismiss={handleCloseRecordList}
          trackedTime={tracked}
          open={openRecordList}
        />
      )}

      <Button
        disabled={isLoggedUserClient && estimated && !tracked}
        className={classNames("show-on-hover c-cooltip c-cooltip--top tw-overflow-visible", {
          open: openForm || tracked || estimated,
          "tw-w-auto tw-max-w-full tw-px-2": tracked || estimated,
        })}
        variant="text gray"
        onClick={handleOpen}
        data-tooltip={tracked || estimated ? t("Edit Time") : t("Add Time")}
      >
        <TimeTaskView
          estimated={estimated}
          tracked={tracked}
        />
      </Button>
    </>
  );
};

TimeEditableTaskView.propTypes = {
  task: PropTypes.object,
  projectId: PropTypes.number,
  isLoggedUserClient: PropTypes.bool,
  tracked: PropTypes.number,
  estimated: PropTypes.number,
};

export const Time = ({ taskId, projectId, isEmpty = false }) => {
  const task = useSelector((state) => getTaskById(state, taskId));
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const tracked = useSelector((state) => getObjectTotalTime(state, "Task", taskId));
  const canViewEstimate = useSelector((state) => canSeeTimeEstimates(state, projectId));

  const estimated = task?.get("estimate") && canViewEstimate ? Number(task.get("estimate")) : 0;

  if (isEmpty) return null;
  if (isLoggedUserClient && !tracked && !estimated) return null;

  return (
    <>
      {task?.get("is_completed") ? (
        <TimeTaskView
          hideIcon={task?.get("is_completed")}
          estimated={estimated}
          tracked={tracked}
        />
      ) : (
        <TimeEditableTaskView
          projectId={projectId}
          estimated={estimated}
          tracked={tracked}
          isLoggedUserClient={isLoggedUserClient}
          task={task}
        />
      )}
    </>
  );
};

Time.propTypes = {
  ...propTypes,
  isEmpty: PropTypes.bool,
};
